import { useEffect, useMemo, useRef, useState } from 'react';
import type { Audit, Speaker } from '../api/types';
import { FilterChip, Select } from './Select';
import { IconSearch } from './ui';

/**
 * Evidence index.
 *
 * Every finding in the audit that carries a verified quote is mapped back to
 * the transcript segment it came from, so the link runs both ways: click a
 * finding to jump to the line, and see on the line which findings cite it.
 */
export interface EvidenceRef {
  segmentIdx: number;
  source: string;
  detail: string;
  quote: string;
}

export function buildEvidenceIndex(audit: Audit): Map<number, EvidenceRef[]> {
  const map = new Map<number, EvidenceRef[]>();
  const add = (idx: number | null, source: string, detail: string, quote: string) => {
    if (idx === null || idx === undefined) return;
    const list = map.get(idx) ?? [];
    if (!list.some((r) => r.source === source && r.quote === quote)) list.push({ segmentIdx: idx, source, detail, quote });
    map.set(idx, list);
  };

  for (const c of Object.values(audit.categoryScores)) {
    for (const e of c.evidence) add(e.segmentIdx, `${c.key.split('_')[0]}. ${c.key.replace(/^[A-J]_/, '').replace(/_/g, ' ')}`, c.note, e.quote);
  }
  for (const key of ['budget', 'authority', 'need', 'timeframe'] as const) {
    const f = audit.bant[key];
    for (const e of f.evidence) add(e.segmentIdx, `BANT · ${key === 'timeframe' ? 'Time frame' : key}`, `${f.verdict} — ${f.reason}`, e.quote);
  }
  for (const f of audit.integrityFlags) {
    if (f.evidence) add(f.evidence.segmentIdx, `Integrity · ${f.label}`, f.explanation, f.evidence.quote);
  }
  for (const f of Object.values(audit.extraction)) {
    if (f.evidence) add(f.evidence.segmentIdx, `Extracted · ${f.label}`, f.value, f.evidence.quote);
  }
  return map;
}

const SPEAKER_FILTERS: { value: Speaker | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'SDR', label: 'SDR only' },
  { value: 'PROSPECT', label: 'Prospect only' },
];

/** Highlight every occurrence of the search term inside a line. */
function Highlighted({ text, term }: { text: string; term: string }) {
  if (!term.trim()) return <>{text}</>;
  const safe = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${safe})`, 'ig'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === term.toLowerCase() ? <mark key={i}>{p}</mark> : <span key={i}>{p}</span>,
      )}
    </>
  );
}

export function TranscriptView({
  audit,
  evidenceIndex,
  targetIdx,
  onClearTarget,
}: {
  audit: Audit;
  evidenceIndex: Map<number, EvidenceRef[]>;
  targetIdx: number | null;
  onClearTarget: () => void;
}) {
  const [search, setSearch] = useState('');
  const [speaker, setSpeaker] = useState<Speaker | 'ALL'>('ALL');
  const [evidenceOnly, setEvidenceOnly] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const segments = audit.transcript.segments;

  const visible = useMemo(
    () =>
      segments.filter((s) => {
        if (speaker !== 'ALL' && s.speaker !== speaker) return false;
        if (evidenceOnly && !evidenceIndex.has(s.idx)) return false;
        if (search.trim() && !s.text.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [segments, speaker, evidenceOnly, search, evidenceIndex],
  );

  // Jumping to evidence must survive an active filter, so clear anything that
  // would hide the target before scrolling to it.
  useEffect(() => {
    if (targetIdx === null) return;
    setEvidenceOnly(false);
    setSpeaker('ALL');
    setSearch('');
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-seg="${targetIdx}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [targetIdx]);

  const matchCount = search.trim() ? visible.length : null;

  return (
    <section className="card">
      <div className="transcript-toolbar">
        <div className="search" style={{ flex: '1 1 220px', maxWidth: 300 }}>
          <label className="sr-only" htmlFor="t-search">
            Search the transcript
          </label>
          <span className="search-icon" aria-hidden>
            <IconSearch size={14} />
          </span>
          <input
            id="t-search"
            type="search"
            placeholder="Search the transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select
          id="t-speaker"
          ariaLabel="Filter by speaker"
          size="sm"
          value={speaker}
          onChange={(v) => setSpeaker(v as Speaker | 'ALL')}
          options={SPEAKER_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
        />

        <FilterChip active={evidenceOnly} onClick={() => setEvidenceOnly(!evidenceOnly)}>
          Cited lines only ({evidenceIndex.size})
        </FilterChip>

        {matchCount !== null && (
          <span className="small dim">
            {matchCount} match{matchCount === 1 ? '' : 'es'}
          </span>
        )}

        {targetIdx !== null && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClearTarget}>
            Clear highlight
          </button>
        )}

        <span className="small dim" style={{ marginLeft: 'auto' }}>
          {audit.transcript.hasDiarization
            ? `${audit.transcript.speakerCount} speakers · ${audit.transcript.speakerMethod.replace(/-/g, ' ')}`
            : 'No speaker attribution'}
        </span>
      </div>

      {!audit.transcript.hasDiarization && (
        <div style={{ padding: '11px 14px 0' }}>
          <div className="alert alert-warn">
            This transcript carries no speaker labels, so no line can be attributed to the SDR or the prospect. Findings that
            depend on who said what are reported as Evidence unavailable rather than scored.
          </div>
        </div>
      )}

      <div className="transcript-body" ref={bodyRef}>
        {visible.length === 0 ? (
          <p className="dim" style={{ padding: '20px 14px' }}>
            No lines match. Clear the search or the filters to see the whole call.
          </p>
        ) : (
          visible.map((s) => {
            const refs = evidenceIndex.get(s.idx);
            const isTarget = targetIdx === s.idx;
            return (
              <div
                key={s.idx}
                data-seg={s.idx}
                className={`turn turn-${s.speaker.toLowerCase()} ${refs ? 'is-evidence' : ''} ${isTarget ? 'is-target' : ''}`}
              >
                <span className="turn-time">{s.timestamp ?? '—'}</span>
                <span className="turn-who">{s.speaker}</span>
                <span className="turn-text">
                  <Highlighted text={s.text} term={search} />
                  {refs && (
                    <span className="turn-evidence-note">
                      Cited by: {refs.map((r) => r.source).join(' · ')}
                    </span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>

      <div className="row small dim" style={{ padding: '10px 14px', borderTop: '1px solid var(--c-border)', flexWrap: 'wrap', gap: 14 }}>
        <span>{audit.transcript.wordCount} words</span>
        <span>{segments.length} turns</span>
        <span>
          Transcription quality: {audit.transcript.quality}
          {audit.transcript.asrConfidence !== null && ` (${Math.round(audit.transcript.asrConfidence * 100)}%)`}
        </span>
        {!audit.transcript.hasTimestamps && <span>No timestamps in the source</span>}
      </div>
    </section>
  );
}
